import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WS_BASE_URL } from '../../utils/apiConfig';
import { EXCHANGE_COLORS, formatExchangeLabel } from './TradesPerMinPanel';

const WINDOW_SIZE = 60; // 60 data points (seconds)

function TradesPerSecPanel() {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const countersRef = useRef({}); // { exchange: { count, long, short } }
  const dataRef = useRef({}); // { exchange: [{ count, long, short, second }...] }
  const intervalRef = useRef(null);
  const [displayData, setDisplayData] = useState(null);

  // Flush counters into data arrays every second
  const tick = useCallback(() => {
    const counters = countersRef.current;
    const data = dataRef.current;
    const now = new Date();
    const second = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

    // Get all known exchanges
    const allExchanges = new Set([...Object.keys(counters), ...Object.keys(data)]);

    for (const exchange of allExchanges) {
      if (!data[exchange]) data[exchange] = [];
      const c = counters[exchange] || { count: 0, long: 0, short: 0 };
      data[exchange].push({ count: c.count, long: c.long, short: c.short, second });
      if (data[exchange].length > WINDOW_SIZE) {
        data[exchange] = data[exchange].slice(-WINDOW_SIZE);
      }
    }

    // Reset counters
    countersRef.current = {};

    // Trigger re-render with snapshot
    const snapshot = {};
    for (const [ex, points] of Object.entries(data)) {
      snapshot[ex] = [...points];
    }
    setDisplayData(snapshot);
  }, []);

  // Connect WebSocket
  useEffect(() => {
    const wsUrl = WS_BASE_URL
      ? `${WS_BASE_URL}`
      : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

    let ws;
    let reconnectTimeout;

    function connect() {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Subscribe with minimal thresholds to get all trades
        ws.send(JSON.stringify({
          type: 'subscribe',
          filters: {
            whaleTradesMinVolume: 0,
            liquidationsMinVolume: 0,
            tradeTypes: ['whale_trade', 'liquidation'],
          },
        }));
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'whale_trade' || msg.type === 'liquidation') {
            const trade = msg.data;
            const exchange = trade?.exchange || 'UNKNOWN';
            const side = trade?.side;
            if (!countersRef.current[exchange]) {
              countersRef.current[exchange] = { count: 0, long: 0, short: 0 };
            }
            countersRef.current[exchange].count += 1;
            // For liquidations: sell = long liquidation, buy = short liquidation
            // (matches redisAggregator convention used by TradesPerMinPanel)
            const isLiq = msg.type === 'liquidation';
            if (isLiq ? side === 'sell' : side === 'buy') {
              countersRef.current[exchange].long += 1;
            } else {
              countersRef.current[exchange].short += 1;
            }
          }
        } catch (_) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = setTimeout(connect, 5000);
      };
    }

    connect();

    // Start 1-second tick
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(reconnectTimeout);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on cleanup
        wsRef.current.close();
      }
    };
  }, [tick]);

  // Draw chart
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const maxDim = 8192;
    canvas.width = Math.min(Math.round(rect.width * dpr), maxDim);
    canvas.height = Math.min(Math.round(rect.height * dpr), maxDim);
    ctx.scale(canvas.width / rect.width, canvas.height / rect.height);

    const w = rect.width;
    const h = rect.height;

    ctx.clearRect(0, 0, w, h);

    if (!displayData || Object.keys(displayData).length === 0) {
      ctx.fillStyle = '#333';
      ctx.font = '11px "DM Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(t('stats.waitingForTrades'), w / 2, h / 2);
      return;
    }

    const exchanges = Object.keys(displayData);
    const padding = { top: 8, right: 8, bottom: 20, left: 32 };
    const chartW = w - padding.left - padding.right;
    const chartH = h - padding.top - padding.bottom;

    // Find max count across all exchanges for Y scale
    let maxCount = 0;
    let maxPoints = 0;
    for (const ex of exchanges) {
      const points = displayData[ex];
      if (points.length > maxPoints) maxPoints = points.length;
      for (const p of points) {
        if (p.count > maxCount) maxCount = p.count;
      }
    }
    if (maxCount === 0) maxCount = 1;

    // Grid lines
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 1;
    const gridLines = 4;
    for (let i = 0; i <= gridLines; i++) {
      const y = padding.top + (chartH / gridLines) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(w - padding.right, y);
      ctx.stroke();
    }

    // Y-axis labels
    ctx.fillStyle = '#555';
    ctx.font = '9px "DM Mono", monospace';
    ctx.textAlign = 'right';
    for (let i = 0; i <= gridLines; i++) {
      const y = padding.top + (chartH / gridLines) * i;
      const val = Math.round(maxCount * (1 - i / gridLines));
      ctx.fillText(String(val), padding.left - 4, y + 3);
    }

    // Draw lines for each exchange
    for (const exchange of exchanges) {
      const points = displayData[exchange];
      if (points.length < 2) continue;

      const color = EXCHANGE_COLORS[exchange] || '#888';
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.8;
      ctx.beginPath();

      for (let i = 0; i < points.length; i++) {
        const x = padding.left + (i / (maxPoints - 1 || 1)) * chartW;
        const y = padding.top + chartH - (points[i].count / maxCount) * chartH;
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // X-axis time labels
    if (maxPoints > 0) {
      ctx.fillStyle = '#444';
      ctx.font = '9px "DM Mono", monospace';
      ctx.textAlign = 'center';
      const firstEx = exchanges[0];
      const pts = displayData[firstEx];
      const labelCount = Math.min(4, pts.length);
      for (let i = 0; i < labelCount; i++) {
        const idx = Math.round((i / (labelCount - 1 || 1)) * (pts.length - 1));
        const x = padding.left + (idx / (maxPoints - 1 || 1)) * chartW;
        ctx.fillText(pts[idx].second, x, h - 4);
      }
    }
  }, [displayData, t]);

  useEffect(() => {
    drawChart();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => drawChart());
    observer.observe(canvas.parentElement);
    return () => observer.disconnect();
  }, [drawChart]);

  // Build L/S ratio pills
  const lsData = {};
  if (displayData) {
    for (const [exchange, points] of Object.entries(displayData)) {
      let totalLong = 0, totalShort = 0;
      for (const p of points) {
        totalLong += p.long || 0;
        totalShort += p.short || 0;
      }
      const total = totalLong + totalShort;
      if (total > 0) {
        lsData[exchange] = {
          longPct: Math.round((totalLong / total) * 100),
          shortPct: Math.round((totalShort / total) * 100),
        };
      }
    }
  }

  const exchanges = displayData ? Object.keys(displayData) : [];

  return (
    <div className="stats-panel stats-tps-panel">
      <div className="stats-panel-header">
        <span className="stats-panel-title">{t('stats.tradesPerSec')}</span>
      </div>

      <div className="stats-tps-chart-wrap">
        <canvas ref={canvasRef} className="stats-tps-canvas" />
      </div>

      {exchanges.length > 0 && (
        <div className="stats-tps-legend">
          {exchanges.map((ex) => (
            <div key={ex} className="stats-tps-legend-item">
              <span
                className="stats-tps-legend-dot"
                style={{ background: EXCHANGE_COLORS[ex] || '#888' }}
              />
              <span className="stats-tps-legend-name">{formatExchangeLabel(ex)}</span>
              {lsData[ex] && (
                <span className="stats-tps-ls-pills">
                  <span className="stats-tps-ls-pill long">{lsData[ex].longPct}%L</span>
                  <span className="stats-tps-ls-pill short">{lsData[ex].shortPct}%S</span>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TradesPerSecPanel;
